<template>
  <div :class="['recipients-tree', currentECircular.type]">
    <div :class="['root', { 'collapse-root': !shouldExpand }]">
      <i
        :class="['tree-arrow pointer', shouldExpand ? 'fa-angle-down' : 'fa-angle-right', 'fa angle']"
        @click="switchArrow"
      />

      <template v-if="isTenantEcircular">
        <span class="tree-zone">{{ floors }}</span>
        <span
          v-if="isViewOperation"
          class="tree-trade-name"
        >
          {{ list.tradeName || $t('emptyValue') }}
        </span>
        <i
          v-else
          class="tree-trade-name"
        >
          <span
            class="pointer"
            @click="navToTenancy(list.id)"
          >
            {{ list.tradeName || $t('emptyValue') }}
          </span>
        </i>
        <span
          v-if="showUserTypeCol"
          class="tree-contact-types"
        />
        <span class="tree-premises">{{ premises }}</span>
        <span
          v-if="showMarketingType"
          class="tree-marketing-type"
        />
      </template>
      <template v-if="isVendorEcircular">
        <span class="tree-recipient-type">
          {{ list.id }}
        </span>
        <span
          v-if="isViewOperation"
          class="tree-company-name"
        >
          {{ list.name || $t('emptyValue') }}
        </span>
        <i
          v-else
          class="tree-company-name"
        >
          <span
            class="pointer"
            @click="navToVendor(list.id)"
          >
            {{ list.name || $t('emptyValue') }}
          </span>
        </i>
      </template>
      <template v-if="isTenancyEcircular">
        <span class="tree-recipient-type" />
        <span
          v-if="isViewOperation"
          class="tree-trade-name"
        >
          {{ list.tradeName || $t('emptyValue') }}
        </span>
        <i
          v-else
          class="tree-trade-name"
        >
          <span
            class="pointer"
            @click="navToTenancy(list.id)"
          >
            {{ list.tradeName || $t('emptyValue') }}
          </span>
        </i>
      </template>

      <i
        v-if="isAddOperation"
        :class="renderChooseIcon(list)"
        @click="clickOneRoot"
      />
      <i
        v-else-if="isDelOperation"
        class="fa fa-times"
        @click="_deleteOneRoot"
      />
    </div>
    <transition-group
      name="expand-leaves"
      tag="div"
    >
      <template v-if="shouldExpand">
        <div
          v-for="leaf in recipients"
          :key="leaf.name+leaf.email+!!(leaf.roleType || []).length"
          class="leaf"
        >
          <span class="tree-arrow-empty" />
          <span
            v-if="isTenantEcircular"
            class="tree-zone-empty"
          />
          <span
            v-if="isTenancyEcircular"
            class="tree-recipient-type"
          >
            {{ leaf.primary ? $t('eCircular.recipientType.master') : $t('eCircular.recipientType.sub') }}
          </span>
          <span :class="isVendorEcircular ? 'tree-vendor-name' : 'tree-user-name'">{{ leaf.name }}</span>
          <span
            v-if="showUserTypeCol"
            class="tree-contact-types"
          >{{ localUserContactTypes(leaf) }}</span>
          <span class="tree-email">{{ leaf.email }}</span>
          <span
            v-if="showMarketingType"
            class="tree-marketing-type"
          >
            {{ (leaf.roleType || []).join(',') }}
          </span>
          <span
            v-if="isVendorEcircular"
            class="tree-recipient-type"
          >
            {{ $t(`eCircular.recipientType.${leaf.userType}`) }}
          </span>
          <i
            v-if="isAddOperation"
            :class="renderChooseIcon(leaf)"
            @click="clickLeaf(leaf)"
          />
          <i
            v-else-if="isDelOperation"
            class="fa fa-times"
            @click="_deleteOneLeaf(leaf)"
          />
        </div>
      </template>
    </transition-group>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'
import lodash from 'lodash'
import mutationTypes from '@/store/mutation-types'
import { TREE_STATUS, operationType } from '@/utils/consts'
import navigation from '@/utils/navigation'
import i18n from '@/utils/i18n'
import { useSelectRecipientsMix } from './hook'

export default {
  name: 'RecipientsTree',
  props: {
    selectAble: {
      type: Boolean,
      default: true,
    },
    list: {
      type: Object,
      default: () => {},
    },
    eCircularType: {
      type: String,
      required: true,
    },
    getTreeStatus: {
      type: Function,
      default: () => {},
    },
    selectOneLeaf: {
      type: Function,
      default: () => {},
    },
    selectOneRoot: {
      type: Function,
      default: () => {},
    },
    operate: {
      type: String,
      default: 'ADD', // DEL
    },
    deleteOneLeaf: {
      type: Function,
      default: () => {},
    },
    deleteOneRoot: {
      type: Function,
      default: () => {},
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    showMarketingType: {
      type: Boolean,
      default: false,
    },
  },
  setup (props) {
    const localUserContactTypes = (leaf) => {
      const userContactTypes = lodash.get(leaf, 'userContactTypes') || []
      return userContactTypes.map(type => i18n.t(`eCircular.userContactType.${type}`)).join(', ')
    }
    const mixSrHooks = useSelectRecipientsMix(props)
    return {
      ...mixSrHooks,
      localUserContactTypes,
      operationType,
    }
  },
  data () {
    return {
      shouldExpand: this.expanded,
    }
  },
  computed: {
    ...mapGetters('ecircular', ['currentECircular', 'navigatingToRecipientOwner']),
    floors () {
      const floorList = lodash.uniq((this.list.premisesDetailList || []).map(detail => detail.floor))
      return floorList.join(', ')
    },
    premises () {
      const premises = this.list.premises || this.$t('emptyValue')
      return `${premises} (${(this.list.sections || []).join(', ')})`
    },
    recipients () {
      const hasRecipients = this.list && this.list.recipients && this.list.recipients.length
      if (hasRecipients && !this.isTenantEcircular) {
        return lodash.sortBy(this.list.recipients, recipient => recipient.primary ? 0 : 1)
      }
      return hasRecipients ? this.list.recipients : []
    },
  },
  mounted () {
    this.saveNavigatingToRecipientOwner(false)
  },
  methods: {
    ...mapMutations('ecircular', {
      saveNavigatingToRecipientOwner: mutationTypes.ECIRCULAR_NAV_TO_RECIPIENT_OWNER,
    }),
    navToTenancy (id) {
      this.saveNavigatingToRecipientOwner(true)
      navigation.toPage('ccdTenancyView', { id, tabIndex: this.isTenantEcircular ? '1' : '4' })
    },
    navToVendor (id) {
      this.saveNavigatingToRecipientOwner(true)
      navigation.toPage('vendor-detail', { id })
    },
    clickOneRoot () {
      this.selectOneRoot(this.list)
    },
    clickLeaf (leaf) {
      this.selectOneLeaf(leaf, this.list)
    },
    _deleteOneRoot () {
      this.deleteOneRoot(this.list)
    },
    _deleteOneLeaf (leaf) {
      this.deleteOneLeaf(leaf, this.list)
    },
    renderChooseIcon (item) {
      let cls = ''
      switch (item.selected) {
        case TREE_STATUS.selected:
          cls = 'selected'
          break
        case TREE_STATUS.unselected:
          cls = 'unselected'
          break
        case TREE_STATUS.added:
          cls = 'added'
          break
        case TREE_STATUS.selectPart:
          cls = 'select-part'
          break
      }
      return `${cls} select`
    },
    switchArrow () {
      this.shouldExpand = !this.shouldExpand
      this.$emit('updateExpanded', this.shouldExpand)
    },
  },
}
</script>

<style scoped lang='scss'>
@import "~styles/recipientsTree";

.recipients-tree {
  border: 1px solid #ededed;
  border-top: none;
  .root,
  .leaf {
    display: flex;
  }
  &.TENANT .leaf span.tree-user-name,
  &.TENANT .leaf span.tree-email {
    padding-left: 8px;
  }
  .root {
    font-family: Verdana;
    font-size: 14px;
    color: #0c3a58;
    padding: 10px 0;

    i.tree-trade-name,
    i.tree-company-name {
      padding-right: 8px;
      span.pointer {
        text-decoration: underline;
      }
    }
  }
  .leaf {
    align-items: center;
    box-sizing: border-box;
  }
  .leaf:last-child {
    margin-bottom: 5px;
  }
  .root span,
  .leaf span:not(.tree-arrow-empty) {
    padding-right: 8px;
  }
  .leaf span {
    word-break: break-word;
  }
  .expand-leaves-enter-active {
    animation: easeIn .3s 0s;
  }
  .expand-leaves-leave-active {
    animation: easeIn .3s 0s reverse;
  }
  .collapse-root {
    background: #FBFBFB;
  }

  @keyframes easeIn {
    0% {
      max-height: 0;
      opacity: 0;
    }
    100% {
      max-height: 80px;
      opacity: 1;
    }
  }
  i.fa.fa-times {
    font-size: 20px;
    color: #aeaeae;
    position: relative;
    right: 5px;
  }
}
</style>
